import { HubConnection, HubConnectionBuilder, HubConnectionState, Subject } from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import RouteService from "@services/routeService";

import {
    ChannelName,
    ConnectionStatus,
    DeviceMessageType,
    DeviceMessageTypeEnum,
    FrontendMessageType,
    LastVideoHwAccel,
    ScreenViewClientCallbacks,
    ScreenViewClientConfig,
    StreamingMode,
    StreamInitMessage,
} from "../types";

import { codecToWebCodecsString, keyStringToEvdevKeycode } from "./ScreenViewClient.utils";

export const highLatencyThresholdMs = 150;

export class ScreenViewClient {
    private connection: HubConnection | null = null;
    private canvas: HTMLCanvasElement | null = null;
    private canvasCtx: CanvasRenderingContext2D | null = null;
    private connected = false;
    private status: ConnectionStatus = ConnectionStatus.Disconnected;
    private config: ScreenViewClientConfig;
    private callbacks: ScreenViewClientCallbacks = {};

    private videoDecoder: VideoDecoder | null = null;
    private decoderGeneration = 0;
    private receivedFirstKeyframe = false;
    private lastVideoCodec: number | null = null;
    private lastVideoWidth = 0;
    private lastVideoHeight = 0;
    private lastVideoHwAccel: LastVideoHwAccel = LastVideoHwAccel.PreferHardware;

    private pingSeq = 0;
    private pendingPings = new Map<number, number>();
    private rttSamples: number[] = [];
    private currentRttMs = 0;
    private pingIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly PING_INTERVAL_MS = 500;
    private readonly MAX_RTT_SAMPLES = 10;
    private readonly HIGH_LATENCY_THRESHOLD_MS = highLatencyThresholdMs;

    private videoEnabled = false;
    private streamingMode: StreamingMode = StreamingMode.Screenshot;
    private lockedToScreenshots = false;

    constructor(config: ScreenViewClientConfig) {
        this.config = config;
    }

    setCanvas(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.canvasCtx = canvas.getContext("2d", {
            willReadFrequently: true,
        });
    }

    onScreenshotReceived(callback: (imageData: ImageData) => void) {
        this.callbacks.onScreenshot = callback;
    }

    onConnectionError(callback: (error: string) => void) {
        this.callbacks.onError = callback;
    }

    onConnectionEstablished(callback: () => void) {
        this.callbacks.onConnected = callback;
    }

    onConnectionLost(callback: () => void) {
        this.callbacks.onDisconnected = callback;
    }

    onStatusChange(callback: (status: ConnectionStatus) => void) {
        this.callbacks.onStatusChange = callback;
    }

    onError(callback: (error: string, metadata?: unknown) => void) {
        this.callbacks.onError = callback;
    }

    onWarning(callback: (warning: string, metadata?: unknown) => void) {
        this.callbacks.onWarning = callback;
    }

    private setStatus(status: ConnectionStatus) {
        if (this.status !== status) {
            this.status = status;
            this.callbacks.onStatusChange?.(status);
        }
    }

    private async getStreamUrl(): Promise<string> {
        return (await RouteService.getStreamRoute()) + "ws?session=" + this.config.sessionId;
    }

    async connect() {
        const url = await this.getStreamUrl();

        if (this.connection) {
            this.connection.stop();
            this.connection = null;
        }

        this.setStatus(ConnectionStatus.Connecting);

        const builder = new HubConnectionBuilder()
            .withUrl(url, { withCredentials: false })
            .withHubProtocol(new MessagePackHubProtocol())
            .withAutomaticReconnect();

        this.connection = builder.build();

        this.connection.on(ChannelName.Receive, (data: ArrayBuffer | Uint8Array) => {
            this.handleReceiveChannel({ data });
        });

        this.connection.on(ChannelName.Connect, () => {
            this.connected = true;
            this.setStatus(ConnectionStatus.Connected);
            this.callbacks.onConnected?.();
        });

        this.connection.on(ChannelName.Disconnect, () => {
            this.connected = false;
            this.setStatus(ConnectionStatus.Disconnected);
            this.callbacks.onDisconnected?.();
        });

        this.connection.onreconnecting(() => {
            this.setStatus(ConnectionStatus.Connecting);
        });

        this.connection.onclose(() => {
            this.connected = false;
            this.setStatus(ConnectionStatus.Disconnected);
            this.callbacks.onDisconnected?.();
            this.stopPingInterval();
            this.cleanupDecoders();
            this.connection = null;
        });

        this.connection
            .start()
            .then(() => {
                this.connected = true;
                this.setStatus(ConnectionStatus.Connected);
                this.callbacks.onConnected?.();

                this.startPingInterval();
            })
            .catch((err: unknown) => {
                this.setStatus(ConnectionStatus.Error);
                const msg = err instanceof Error ? err.message : "Connection error";
                this.callbacks.onError?.(msg, err);
            });
    }

    private isTransportReady(): boolean {
        return this.connection?.state === HubConnectionState.Connected;
    }

    private handleReceiveChannel(event: { data: ArrayBuffer | ArrayBufferView }) {
        let data: Uint8Array;
        if (event.data instanceof ArrayBuffer) {
            data = new Uint8Array(event.data);
        } else {
            const v = event.data as ArrayBufferView;
            data = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
        }
        if (data.length === 0) {
            return;
        }

        const msgType = data[0] as DeviceMessageTypeEnum;

        if (msgType === DeviceMessageType.VideoFrame) {
            this.handleVideoFrame(data);
        } else if (msgType === DeviceMessageType.Screenshot) {
            this.handleScreenshot(data);
        } else if (msgType === DeviceMessageType.Pong) {
            this.handlePong(data);
        } else if (msgType === DeviceMessageType.Init) {
            this.handleInitMessage(data);
        } else {
            this.callbacks.onWarning?.("Unknown message type", msgType);
        }
    }

    private handleInitMessage(data: Uint8Array) {
        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        const codec = data[1];
        const width = view.getUint16(2, false);
        const height = view.getUint16(4, false);

        this.lastVideoWidth = width;
        this.lastVideoHeight = height;
        this.lastVideoCodec = codec;

        if (this.canvas) {
            this.canvas.width = width;
            this.canvas.height = height;
        }

        this.initVideoDecoder({ codec, width, height });
    }

    private handleVideoFrame(data: Uint8Array) {
        if (!this.videoDecoder || this.videoDecoder.state !== "configured") {
            return;
        }

        if (data.length < 15) {
            this.callbacks.onError?.("Video frame too short", data.length);
            return;
        }

        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        const _msgType = data[0];
        const _codec = data[1];
        const flags = data[2];
        const isKeyframe = (flags & 0x01) !== 0;
        const ptsUs = view.getBigUint64(3, false);

        const frameData = data.slice(15);

        if (!this.receivedFirstKeyframe) {
            if (!isKeyframe) {
                return;
            }
            this.receivedFirstKeyframe = true;
        }

        try {
            const timestamp = Number(ptsUs);
            if (!Number.isSafeInteger(timestamp)) {
                this.callbacks.onWarning?.("Timestamp may lose precision", ptsUs);
            }

            const chunk = new EncodedVideoChunk({
                type: isKeyframe ? "key" : "delta",
                timestamp: timestamp,
                data: frameData,
            });

            this.videoDecoder.decode(chunk);
        } catch (e) {
            this.callbacks.onError?.("Failed to decode video chunk", e);

            this.receivedFirstKeyframe = false;

            if (isKeyframe) {
                this.callbacks.onWarning?.("Keyframe decode failed, attempting decoder recovery");

                if (this.lastVideoCodec !== null && this.lastVideoWidth > 0 && this.lastVideoHeight > 0) {
                    this.initVideoDecoder({
                        codec: this.lastVideoCodec,
                        width: this.lastVideoWidth,
                        height: this.lastVideoHeight,
                    }).catch((err) => this.callbacks.onError?.("Failed to recover video decoder", err));
                }
            }
        }
    }

    private async initVideoDecoder(message: StreamInitMessage) {
        const codec = message.codec;
        const width = Number(message.width);
        const height = Number(message.height);

        if (!("VideoDecoder" in window)) {
            this.callbacks.onError?.("WebCodecs not supported in this browser");
            return;
        }

        const thisGeneration = ++this.decoderGeneration;

        this.lastVideoCodec = codec;
        this.lastVideoWidth = width;
        this.lastVideoHeight = height;

        const codecString = codecToWebCodecsString(codec);

        let useHardwareAcceleration: LastVideoHwAccel = LastVideoHwAccel.PreferHardware;
        try {
            const hwSupport = await VideoDecoder.isConfigSupported({
                codec: codecString,
                codedWidth: width,
                codedHeight: height,
                hardwareAcceleration: "prefer-hardware",
            });

            if (!hwSupport.supported) {
                const swSupport = await VideoDecoder.isConfigSupported({
                    codec: codecString,
                    codedWidth: width,
                    codedHeight: height,
                });

                if (!swSupport.supported) {
                    this.callbacks.onError?.(`Video codec ${codecString} not supported`);
                    return;
                }
                useHardwareAcceleration = LastVideoHwAccel.NoPreference;
            }
        } catch (e) {
            this.callbacks.onError?.("Failed to check codec support", e);
        }

        if (thisGeneration !== this.decoderGeneration) {
            return;
        }

        this.lastVideoHwAccel = useHardwareAcceleration;

        if (this.videoDecoder) {
            try {
                this.videoDecoder.close();
            } catch (e) {
                this.callbacks.onError?.("Failed to close video decoder", e);
            }
        }

        this.videoDecoder = new VideoDecoder({
            output: (frame: VideoFrame) => {
                if (thisGeneration !== this.decoderGeneration) {
                    frame.close();
                    return;
                }
                this.renderVideoFrame(frame);
            },
            error: (e: Error) => {
                if (thisGeneration !== this.decoderGeneration) {
                    return;
                }

                this.callbacks.onError?.("Video decoder error, will wait for next keyframe", e);
                this.receivedFirstKeyframe = false;
            },
        });

        const config: VideoDecoderConfig = {
            codec: codecString,
            codedWidth: width,
            codedHeight: height,
            hardwareAcceleration: useHardwareAcceleration,
        };

        try {
            this.videoDecoder.configure(config);
        } catch (e) {
            this.callbacks.onError?.("Failed to configure video decoder", e);
            try {
                this.videoDecoder.configure({
                    codec: codecString,
                    codedWidth: width,
                    codedHeight: height,
                    hardwareAcceleration: LastVideoHwAccel.NoPreference,
                });
            } catch (e2) {
                this.callbacks.onError?.("Failed to configure video decoder with fallback", e2);
            }
        }
    }

    private async handleScreenshot(data: Uint8Array) {
        if (data.length < 2) {
            this.callbacks.onWarning?.("Screenshot message too short", data.length);
            return;
        }

        const screenshotData = data.slice(1);
        const screenshotBuffer = screenshotData.buffer.slice(
            screenshotData.byteOffset,
            screenshotData.byteOffset + screenshotData.byteLength,
        );

        try {
            const blob = new Blob([screenshotBuffer], { type: "image/jpeg" });
            const imageUrl = URL.createObjectURL(blob);

            const img = new Image();

            img.onload = () => {
                if (this.canvas && this.canvasCtx) {
                    if (this.canvas.width !== img.width || this.canvas.height !== img.height) {
                        this.canvas.width = img.width;
                        this.canvas.height = img.height;
                    }

                    this.canvasCtx.drawImage(img, 0, 0);
                    const imageData = this.canvasCtx.getImageData(0, 0, img.width, img.height);
                    this.callbacks.onScreenshot?.(imageData);
                }

                URL.revokeObjectURL(imageUrl);
            };

            img.onerror = () => {
                URL.revokeObjectURL(imageUrl);
                this.callbacks.onError?.("Failed to decode screenshot");
            };

            img.src = imageUrl;
        } catch (e) {
            this.callbacks.onError?.("Failed to process screenshot data", e);
        }
    }

    private sendPing() {
        if (!this.isTransportReady()) {
            return;
        }

        const seq = this.pingSeq++ % 256;
        const sendTime = performance.now();
        this.pendingPings.set(seq, sendTime);

        this.sendMessage(new Uint8Array([FrontendMessageType.Ping, seq]));
    }

    private handlePong(data: Uint8Array) {
        if (data.length < 2) {
            this.callbacks.onWarning?.("Pong message too short", data.length);
            return;
        }

        const seq = data[1];

        const sendTime = this.pendingPings.get(seq);
        if (sendTime === undefined) {
            this.callbacks.onWarning?.("Received pong for unknown seq", seq);
            return;
        }
        this.pendingPings.delete(seq);
        const receiveTime = performance.now();
        const rtt = receiveTime - sendTime;
        this.rttSamples.push(rtt);
        if (this.rttSamples.length > this.MAX_RTT_SAMPLES) {
            this.rttSamples.shift();
        }
        const sum = this.rttSamples.reduce((a, b) => a + b, 0);
        this.currentRttMs = sum / this.rttSamples.length;
        this.updateAdaptiveSwitching();
    }

    private sendMessage(payload: Uint8Array): void {
        if (this.connection) {
            const subject = new Subject<Uint8Array>();
            this.connection.send(ChannelName.Send, subject);
            subject.next(payload);
            subject.complete();
        }
    }

    private startPingInterval() {
        this.stopPingInterval();

        this.sendPing();

        this.pingIntervalId = setInterval(() => {
            this.sendPing();

            const now = performance.now();
            for (const [seq, sendTime] of this.pendingPings.entries()) {
                if (now - sendTime > 5000) {
                    this.pendingPings.delete(seq);
                }
            }
        }, this.PING_INTERVAL_MS);
    }

    private stopPingInterval() {
        if (this.pingIntervalId) {
            clearInterval(this.pingIntervalId);
            this.pingIntervalId = null;
        }
        this.pendingPings.clear();
    }

    private updateAdaptiveSwitching() {
        if (this.streamingMode !== StreamingMode.Adaptive) {
            return;
        }

        if (this.lockedToScreenshots) {
            return;
        }

        if (this.currentRttMs < this.HIGH_LATENCY_THRESHOLD_MS - 20 && !this.videoEnabled && !this.lockedToScreenshots) {
            this.switchToScreenshots();
        }
    }

    private switchToScreenshots() {
        if (!this.videoEnabled) {
            return;
        }

        this.videoEnabled = false;
        this.lockedToScreenshots = true;
        this.setVideoEnabled(0);
    }

    private switchToH264() {
        if (this.videoEnabled) {
            return;
        }

        this.videoEnabled = true;
        this.lockedToScreenshots = false;
        this.setVideoEnabled(1);
    }

    public retryH264() {
        if (this.streamingMode === StreamingMode.Adaptive && this.lockedToScreenshots) {
            this.lockedToScreenshots = false;
            this.switchToH264();
        }
    }

    private renderVideoFrame(frame: VideoFrame) {
        if (!this.canvas || !this.canvasCtx) {
            frame.close();
            return;
        }

        if (this.canvas.width !== frame.displayWidth || this.canvas.height !== frame.displayHeight) {
            this.canvas.width = frame.displayWidth;
            this.canvas.height = frame.displayHeight;
        }

        this.canvasCtx.drawImage(frame, 0, 0);
        frame.close();
    }

    private cleanupDecoders() {
        if (this.videoDecoder) {
            try {
                this.videoDecoder.close();
            } catch (e) {
                this.callbacks.onError?.("Failed to close video decoder", e);
            }
            this.videoDecoder = null;
        }
        this.receivedFirstKeyframe = false;
    }

    private setVideoEnabled(enabled: number) {
        if (!this.isTransportReady()) {
            return;
        }

        this.sendMessage(new Uint8Array([FrontendMessageType.ControlMessage, enabled]));
    }

    sendMouseClick(x: number, y: number, button: number, isDown: number) {
        if (!this.connected || !this.isTransportReady()) {
            return;
        }

        const xByte = Math.max(0, Math.min(255, Math.round(x)));
        const yByte = Math.max(0, Math.min(255, Math.round(y)));
        const buttonByte = Math.max(0, Math.min(255, button));
        const isDownByte = Math.max(0, Math.min(255, isDown));

        this.sendMessage(new Uint8Array([FrontendMessageType.MouseClick, xByte, yByte, buttonByte, isDownByte]));
    }

    sendMouseMove(x: number, y: number) {
        if (!this.connected || !this.isTransportReady()) {
            return;
        }

        const xByte = Math.max(0, Math.min(255, Math.round(x)));
        const yByte = Math.max(0, Math.min(255, Math.round(y)));

        this.sendMessage(new Uint8Array([FrontendMessageType.MouseMove, xByte, yByte]));
    }

    sendMouseWheel(deltaX: number, deltaY: number) {
        if (!this.connected || !this.isTransportReady()) {
            return;
        }

        const deltaXByte = Math.max(0, Math.min(255, Math.round(deltaX)));
        const deltaYByte = Math.max(0, Math.min(255, Math.round(deltaY)));

        this.sendMessage(new Uint8Array([FrontendMessageType.MouseWheel, deltaXByte, deltaYByte]));
    }

    sendKey(
        key: string,
        isDown: number,
        modifiers: {
            ctrl: number;
            alt: number;
            shift: number;
            meta: number;
        },
    ) {
        if (!this.connected || !this.isTransportReady()) {
            return;
        }

        const evdevKeycode = keyStringToEvdevKeycode(key);
        if (evdevKeycode === null) {
            this.callbacks.onWarning?.("Unknown key, cannot convert to evdev keycode", key);
            return;
        }

        const modifiersByte =
            (modifiers.shift ? 1 : 0) | (modifiers.ctrl ? 2 : 0) | (modifiers.alt ? 4 : 0) | (modifiers.meta ? 8 : 0);

        const buffer = new Uint8Array(6);
        const view = new DataView(buffer.buffer);
        buffer[0] = FrontendMessageType.KeyboardInput;
        buffer[1] = 0;
        buffer[2] = isDown;
        buffer[3] = modifiersByte;
        view.setUint16(4, evdevKeycode, false);

        this.sendMessage(buffer);
    }

    disconnect() {
        this.stopPingInterval();
        this.cleanupDecoders();

        if (this.connection) {
            this.connection.stop();
            this.connection = null;
        }

        this.connected = false;
        this.setStatus(ConnectionStatus.Disconnected);
    }

    isConnected(): boolean {
        return this.connected && this.isTransportReady();
    }

    getStatus(): ConnectionStatus {
        return this.status;
    }

    getCurrentRTT(): number {
        return this.currentRttMs;
    }

    getStreamingMode(): StreamingMode {
        return this.streamingMode;
    }

    setStreamingMode(mode: StreamingMode) {
        this.streamingMode = mode;
        if (mode === StreamingMode.H264) {
            this.videoEnabled = true;
            this.lockedToScreenshots = false;
            this.setVideoEnabled(1);
        } else if (mode === StreamingMode.Screenshot) {
            this.videoEnabled = false;
            this.lockedToScreenshots = true;
            this.setVideoEnabled(0);
        } else if (mode === StreamingMode.Adaptive) {
            this.lockedToScreenshots = false;
            this.updateAdaptiveSwitching();
        }
    }
}
